import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, Divider, IconButton, Tooltip, Switch, Snackbar,
  LinearProgress, Select, MenuItem, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Email, Refresh, CheckCircle, Error as ErrorIcon, Save,
  PowerSettingsNew, Speed, Send, Timeline, Shield,
  MarkEmailRead, SignalCellularAlt, DraftsOutlined,
  Dns, Lock,
} from '@mui/icons-material';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// ═══════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, sub }) => (
  <Card sx={{
    borderRadius: 2, border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20` },
  }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8', mt: 0.2 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Section = ({ icon, title, color, children, action }) => (
  <Box>
    <Box className="flex items-center justify-between mb-1.5">
      <Box className="flex items-center gap-1.5">
        {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', minWidth: 80 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// EMAIL TEMPLATES
// ═══════════════════════════════════════════════════════════════

const EMAIL_TEMPLATES = [
  { name: 'Policy Issued', type: 'Policy', description: 'Notification when insurance policy is issued to farmer', subject: 'Your MeenSure Policy Has Been Issued' },
  { name: 'KYC Approved', type: 'KYC', description: 'Login credentials sent when KYC verification is approved', subject: 'KYC Verification Approved — Your Credentials' },
  { name: 'Credit Activated', type: 'Credit', description: 'Notification when credit application is approved and disbursed', subject: 'Credit Application Approved' },
  { name: 'Harvest Completed', type: 'Harvest', description: 'Notification when harvest record is finalized', subject: 'Harvest Record Completed' },
  { name: 'Claim Status Update', type: 'Claim', description: 'Notification on claim status changes (submitted, approved, rejected)', subject: 'Claim Status Update' },
  { name: 'Payment Receipt', type: 'Payment', description: 'Premium payment confirmation receipt', subject: 'Payment Received — MeenSure' },
];

const TEMPLATE_TYPE_COLORS = {
  Policy: { bg: '#dcfce7', color: '#16a34a' },
  KYC: { bg: '#dbeafe', color: '#2563eb' },
  Credit: { bg: '#f3e8ff', color: '#7c3aed' },
  Harvest: { bg: '#fef3c7', color: '#d97706' },
  Claim: { bg: '#fef2f2', color: '#dc2626' },
  Payment: { bg: '#ccfbf1', color: '#0891b2' },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const EmailSettings = () => {
  // SMTP Settings (local state, no backend)
  const [smtpSettings, setSmtpSettings] = useState({
    smtpHost: 'smtp.gmail.com',
    smtpPort: '587',
    smtpUser: 'noreply@piscicare.com',
    smtpPassword: '',
    senderName: 'Piscicare Insurance',
    senderEmail: 'noreply@piscicare.com',
    enableSSL: true,
    enableTLS: true,
  });

  // Feature toggles
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [policyNotif, setPolicyNotif] = useState(true);
  const [claimNotif, setClaimNotif] = useState(true);
  const [kycNotif, setKycNotif] = useState(true);
  const [creditNotif, setCreditNotif] = useState(true);

  // Test & health
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTimestamp, setTestTimestamp] = useState(null);
  const [smtpConnected, setSmtpConnected] = useState(null); // null=unknown, true, false
  const [lastSentTime, setLastSentTime] = useState(null);

  // Stats (session tracked)
  const [emailStats, setEmailStats] = useState({ sentToday: 0, failedToday: 0 });
  const [emailLogs, setEmailLogs] = useState([]);

  // Audit log
  const [auditLog, setAuditLog] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Saving
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ── Audit helper ──
  const addAudit = useCallback((action, detail) => {
    setAuditLog(prev => [
      { timestamp: new Date().toISOString(), action, detail },
      ...prev.slice(0, 29),
    ]);
  }, []);

  // ── Handlers ──
  const handleSmtpChange = (field) => (event) => {
    setSmtpSettings(prev => ({
      ...prev,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }));
  };

  const handleSaveSmtp = () => {
    setSaving(true);
    // Simulate save
    setTimeout(() => {
      setSaving(false);
      setSnackbar({ open: true, message: 'SMTP settings saved successfully!', severity: 'success' });
      addAudit('SMTP Saved', `Host: ${smtpSettings.smtpHost}:${smtpSettings.smtpPort}`);
    }, 800);
  };

  const handleTestEmail = () => {
    if (!testEmail.trim() || !testEmail.includes('@')) {
      setSnackbar({ open: true, message: 'Enter a valid email address', severity: 'error' });
      return;
    }
    setTestSending(true);
    setTestResult(null);

    // Simulate SMTP test send
    setTimeout(() => {
      const success = smtpSettings.smtpHost && smtpSettings.smtpUser;
      const now = new Date().toISOString();

      setTestResult({
        success,
        message: success ? 'Test email sent successfully via SMTP' : 'SMTP connection failed — check host and credentials',
      });
      setTestTimestamp(now);
      setSmtpConnected(success);

      if (success) {
        setLastSentTime(now);
        setEmailStats(prev => ({ ...prev, sentToday: prev.sentToday + 1 }));
        setEmailLogs(prev => [
          { to: testEmail, type: 'Test', status: 'Delivered', timestamp: now },
          ...prev.slice(0, 9),
        ]);
      } else {
        setEmailStats(prev => ({ ...prev, failedToday: prev.failedToday + 1 }));
        setEmailLogs(prev => [
          { to: testEmail, type: 'Test', status: 'Failed', timestamp: now },
          ...prev.slice(0, 9),
        ]);
      }

      addAudit('Test Email', `Sent to ${testEmail} — ${success ? 'Delivered' : 'Failed'}`);
      setTestSending(false);
    }, 1200);
  };

  const handleToggleNotifications = () => {
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);
    setSnackbar({ open: true, message: `Email notifications ${newState ? 'enabled' : 'disabled'}`, severity: 'success' });
    addAudit('Notifications Toggled', `Email notifications ${newState ? 'enabled' : 'disabled'}`);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setSnackbar({ open: true, message: 'Dashboard refreshed', severity: 'info' });
    }, 600);
  };

  // Delivery rate
  const deliveryRate = useMemo(() => {
    const total = emailStats.sentToday + emailStats.failedToday;
    if (total === 0) return '—';
    return `${Math.round((emailStats.sentToday / total) * 100)}%`;
  }, [emailStats]);

  // Template statuses (derived from notification toggles)
  const templates = useMemo(() => {
    return EMAIL_TEMPLATES.map(t => {
      let enabled = notificationsEnabled;
      if (t.type === 'Policy' || t.type === 'Payment') enabled = enabled && policyNotif;
      if (t.type === 'Claim') enabled = enabled && claimNotif;
      if (t.type === 'KYC') enabled = enabled && kycNotif;
      if (t.type === 'Credit') enabled = enabled && creditNotif;
      return { ...t, enabled };
    });
  }, [notificationsEnabled, policyNotif, claimNotif, kycNotif, creditNotif]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ HEADER ══════════ */}
      <Box className="flex items-center justify-between mb-3">
        <Box className="flex items-center gap-2">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Email sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Email Operations Control Panel
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              SMTP Configuration, Templates & Delivery Monitoring
            </Typography>
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {/* 5️⃣ Enable/Disable notification toggle */}
          <Box className="flex items-center gap-1.5" sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: notificationsEnabled ? '#dcfce7' : '#fef2f2', border: `1px solid ${notificationsEnabled ? '#22c55e' : '#fca5a5'}` }}>
            <PowerSettingsNew sx={{ fontSize: 16, color: notificationsEnabled ? '#16a34a' : '#dc2626' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: notificationsEnabled ? '#16a34a' : '#dc2626' }}>
              {notificationsEnabled ? 'Notifications On' : 'Notifications Off'}
            </Typography>
            <Switch
              size="small" checked={notificationsEnabled}
              onChange={handleToggleNotifications}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' },
              }}
            />
          </Box>
          <Tooltip title="Refresh"><IconButton size="small" onClick={handleRefresh} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* ══════════ 1. SMTP HEALTH CARD ══════════ */}
      <Card sx={{
        borderRadius: 2, mb: 2.5, overflow: 'hidden',
        border: `2px solid ${smtpConnected === true ? '#22c55e' : smtpConnected === false ? '#fca5a5' : '#e2e8f0'}`,
      }}>
        <Box sx={{ px: 2, py: 1.2, bgcolor: smtpConnected === true ? '#f0fdf4' : smtpConnected === false ? '#fef2f2' : '#f8fafc' }}>
          <Box className="flex items-center justify-between">
            <Box className="flex items-center gap-2">
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: smtpConnected === true ? '#dcfce7' : smtpConnected === false ? '#fecaca' : '#e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {smtpConnected === true
                  ? <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} />
                  : smtpConnected === false
                    ? <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                    : <Dns sx={{ fontSize: 18, color: '#94a3b8' }} />}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: smtpConnected === true ? '#16a34a' : smtpConnected === false ? '#dc2626' : '#475569' }}>
                  {smtpConnected === true ? 'SMTP Connected' : smtpConnected === false ? 'SMTP Connection Failed' : 'SMTP Status Unknown'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                  {smtpConnected === true ? `Connected to ${smtpSettings.smtpHost}:${smtpSettings.smtpPort}` : smtpConnected === false ? 'Check SMTP credentials and host configuration' : 'Send a test email to verify SMTP connectivity'}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-1.5">
              <Chip
                label={smtpConnected === true ? 'Connected' : smtpConnected === false ? 'Failed' : 'Unknown'}
                size="small"
                sx={{
                  height: 20, fontSize: '0.58rem', fontWeight: 700,
                  bgcolor: smtpConnected === true ? '#dcfce7' : smtpConnected === false ? '#fef2f2' : '#f1f5f9',
                  color: smtpConnected === true ? '#16a34a' : smtpConnected === false ? '#dc2626' : '#94a3b8',
                }} />
              <Chip
                label={smtpSettings.enableTLS ? 'TLS' : smtpSettings.enableSSL ? 'SSL' : 'Plain'}
                size="small"
                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#2563eb' }} />
            </Box>
          </Box>
        </Box>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
            <InfoRow label="Host" value={`${smtpSettings.smtpHost}:${smtpSettings.smtpPort}`} />
            <InfoRow label="User" value={smtpSettings.smtpUser} />
            <InfoRow label="Last Test" value={
              testResult
                ? <Box className="flex items-center gap-1">
                    {testResult.success ? <CheckCircle sx={{ fontSize: 11, color: '#16a34a' }} /> : <ErrorIcon sx={{ fontSize: 11, color: '#dc2626' }} />}
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: testResult.success ? '#16a34a' : '#dc2626' }}>
                      {testResult.success ? 'Passed' : 'Failed'}
                    </Typography>
                    {testTimestamp && (
                      <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', ml: 0.5 }}>{fmtTime(testTimestamp)}</Typography>
                    )}
                  </Box>
                : <Typography sx={{ fontSize: '0.66rem', color: '#cbd5e1' }}>Not tested yet</Typography>
            } />
            <InfoRow label="Last Sent" value={lastSentTime ? fmtDateTime(lastSentTime) : 'No emails sent'} />
          </Box>
        </CardContent>
      </Card>

      {/* ══════════ 2. EMAIL STATS KPI STRIP ══════════ */}
      <Box className="grid grid-cols-3 gap-2.5 mb-3">
        <KPICard icon={<Send />} label="Sent Today" color="#2563eb"
          value={emailStats.sentToday} sub="Session tracked" />
        <KPICard icon={<ErrorIcon />} label="Failed Today" color="#dc2626"
          value={emailStats.failedToday} sub="Session tracked" />
        <KPICard icon={<SignalCellularAlt />} label="Delivery Rate" color="#16a34a"
          value={deliveryRate} />
      </Box>

      {/* ══════════ TWO-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>

        {/* ── LEFT COLUMN ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 3️⃣ Email Logs Table (last 10) */}
          <Section icon={<DraftsOutlined />} title="Email Logs (Last 10)" color="#2563eb"
            action={emailLogs.length > 0 && (
              <Button size="small" onClick={() => setEmailLogs([])}
                sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>Clear</Button>
            )}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              {emailLogs.length === 0 ? (
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                  <MarkEmailRead sx={{ fontSize: 32, color: '#2563eb', opacity: 0.3, mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8' }}>
                    No email activity yet
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', mt: 0.3 }}>
                    Send a test email to see delivery logs here.
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Timestamp</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>To</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Type</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailLogs.map((log, i) => (
                        <TableRow key={i} sx={{ bgcolor: i === 0 ? (log.status === 'Delivered' ? '#f0fdf4' : '#fef2f2') : 'transparent' }}>
                          <TableCell sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#64748b', py: 0.8, whiteSpace: 'nowrap' }}>
                            {fmtDateTime(log.timestamp)}
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>{log.to}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={log.type} size="small"
                              sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#2563eb' }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={log.status} size="small"
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 700,
                                bgcolor: log.status === 'Delivered' ? '#dcfce7' : '#fef2f2',
                                color: log.status === 'Delivered' ? '#16a34a' : '#dc2626',
                              }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Section>

          {/* 4️⃣ Email Template Manager */}
          <Section icon={<Email />} title="Email Template Manager" color="#7c3aed">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <TableContainer sx={{ maxHeight: 340 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Template</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Type</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Subject</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((t, i) => {
                      const typeColor = TEMPLATE_TYPE_COLORS[t.type] || { bg: '#f1f5f9', color: '#64748b' };
                      return (
                        <TableRow key={i} hover>
                          <TableCell sx={{ py: 0.8 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b' }}>{t.name}</Typography>
                            <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8' }}>{t.description}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={t.type} size="small"
                              sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: typeColor.bg, color: typeColor.color }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Typography sx={{ fontSize: '0.62rem', color: '#475569', fontStyle: 'italic' }}>{t.subject}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={t.enabled ? 'Active' : 'Disabled'} size="small"
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 700,
                                bgcolor: t.enabled ? '#dcfce7' : '#fef2f2',
                                color: t.enabled ? '#16a34a' : '#dc2626',
                              }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Notification category toggles */}
              <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1 }}>
                  Notification Categories
                </Typography>
                <Box className="flex flex-wrap gap-2">
                  {[
                    { label: 'Policy', checked: policyNotif, onChange: () => { setPolicyNotif(!policyNotif); addAudit('Category Toggle', `Policy notifications ${!policyNotif ? 'enabled' : 'disabled'}`); } },
                    { label: 'Claims', checked: claimNotif, onChange: () => { setClaimNotif(!claimNotif); addAudit('Category Toggle', `Claim notifications ${!claimNotif ? 'enabled' : 'disabled'}`); } },
                    { label: 'KYC', checked: kycNotif, onChange: () => { setKycNotif(!kycNotif); addAudit('Category Toggle', `KYC notifications ${!kycNotif ? 'enabled' : 'disabled'}`); } },
                    { label: 'Credit', checked: creditNotif, onChange: () => { setCreditNotif(!creditNotif); addAudit('Category Toggle', `Credit notifications ${!creditNotif ? 'enabled' : 'disabled'}`); } },
                  ].map((cat) => (
                    <Box key={cat.label} className="flex items-center gap-0.5" sx={{
                      px: 1, py: 0.3, borderRadius: 1.5, cursor: 'pointer',
                      bgcolor: cat.checked ? '#dcfce7' : '#fef2f2',
                      border: `1px solid ${cat.checked ? '#86efac' : '#fecaca'}`,
                    }} onClick={cat.onChange}>
                      <Switch size="small" checked={cat.checked && notificationsEnabled} disabled={!notificationsEnabled}
                        sx={{ transform: 'scale(0.7)', '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }}
                        onChange={cat.onChange}
                      />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: cat.checked ? '#16a34a' : '#dc2626' }}>{cat.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
          </Section>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Test Email */}
          <Section icon={<Speed />} title="Test Email" color="#7c3aed">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Recipient</Typography>
                <TextField
                  fullWidth size="small" type="email"
                  placeholder="test@example.com"
                  value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                />

                <Button fullWidth variant="contained"
                  startIcon={testSending ? null : <Send />}
                  onClick={handleTestEmail}
                  disabled={testSending || !testEmail.trim()}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, fontSize: '0.75rem' }}>
                  {testSending ? 'Sending…' : 'Send Test Email'}
                </Button>

                {testResult && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: testResult.success ? '#f0fdf4' : '#fef2f2' }}>
                    <Box className="flex items-center gap-1">
                      {testResult.success
                        ? <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} />
                        : <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: testResult.success ? '#16a34a' : '#dc2626' }}>
                        {testResult.success ? 'Test Email Sent' : 'Delivery Failed'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#64748b', mt: 0.5 }}>{testResult.message}</Typography>
                    {testTimestamp && (
                      <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', mt: 0.3 }}>{fmtDateTime(testTimestamp)}</Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Section>

          {/* SMTP Configuration */}
          <Section icon={<Lock />} title="SMTP Configuration" color="#0f172a">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>SMTP Host</Typography>
                  <TextField fullWidth size="small" value={smtpSettings.smtpHost} onChange={handleSmtpChange('smtpHost')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }} />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Port</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={smtpSettings.smtpPort} onChange={handleSmtpChange('smtpPort')}
                      sx={{ borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' }}>
                      <MenuItem value="25">25 (Standard)</MenuItem>
                      <MenuItem value="465">465 (SSL)</MenuItem>
                      <MenuItem value="587">587 (TLS)</MenuItem>
                      <MenuItem value="2525">2525 (Alt)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Username</Typography>
                  <TextField fullWidth size="small" value={smtpSettings.smtpUser} onChange={handleSmtpChange('smtpUser')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }} />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Password</Typography>
                  <TextField fullWidth size="small" type="password" placeholder="SMTP password"
                    value={smtpSettings.smtpPassword} onChange={handleSmtpChange('smtpPassword')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }} />
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Sender Name</Typography>
                  <TextField fullWidth size="small" value={smtpSettings.senderName} onChange={handleSmtpChange('senderName')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }} />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Sender Email</Typography>
                  <TextField fullWidth size="small" value={smtpSettings.senderEmail} onChange={handleSmtpChange('senderEmail')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }} />
                </Box>

                {/* SSL/TLS toggles */}
                <Box className="flex gap-3 mb-1.5">
                  <Box className="flex items-center gap-0.5" sx={{ px: 1, py: 0.3, borderRadius: 1.5, bgcolor: smtpSettings.enableTLS ? '#dcfce7' : '#f1f5f9', border: `1px solid ${smtpSettings.enableTLS ? '#86efac' : '#e2e8f0'}` }}>
                    <Switch size="small" checked={smtpSettings.enableTLS} onChange={handleSmtpChange('enableTLS')}
                      sx={{ transform: 'scale(0.7)', '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>TLS</Typography>
                  </Box>
                  <Box className="flex items-center gap-0.5" sx={{ px: 1, py: 0.3, borderRadius: 1.5, bgcolor: smtpSettings.enableSSL ? '#dcfce7' : '#f1f5f9', border: `1px solid ${smtpSettings.enableSSL ? '#86efac' : '#e2e8f0'}` }}>
                    <Switch size="small" checked={smtpSettings.enableSSL} onChange={handleSmtpChange('enableSSL')}
                      sx={{ transform: 'scale(0.7)', '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>SSL</Typography>
                  </Box>
                </Box>

                <Button fullWidth variant="contained"
                  startIcon={saving ? null : <Save />}
                  onClick={handleSaveSmtp}
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontSize: '0.75rem' }}>
                  {saving ? 'Saving…' : 'Save SMTP Settings'}
                </Button>
              </CardContent>
            </Card>
          </Section>
        </Box>
      </Box>

      {/* ══════════ AUDIT LOG ══════════ */}
      <Box sx={{ mt: 2.5 }}>
        <Section icon={<Timeline />} title="Audit Log" color="#64748b"
          action={auditLog.length > 0 && (
            <Button size="small" onClick={() => setAuditLog([])} sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>Clear</Button>
          )}>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Static info */}
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: notificationsEnabled ? '#16a34a' : '#dc2626', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                    Email Notifications {notificationsEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Current state</Typography>
                </Box>
              </Box>
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: smtpConnected === true ? '#16a34a' : '#94a3b8', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                    SMTP: {smtpSettings.smtpHost}:{smtpSettings.smtpPort}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    Status: {smtpConnected === true ? 'Connected' : smtpConnected === false ? 'Failed' : 'Not tested'}
                  </Typography>
                </Box>
              </Box>

              {/* Session audit entries */}
              {auditLog.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1 }}>
                    Session Activity
                  </Typography>
                  {auditLog.map((entry, i) => (
                    <Box key={i} className="flex items-start gap-2 mb-1.5">
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#64748b', mt: 0.6, flexShrink: 0 }} />
                      <Box className="min-w-0 flex-1">
                        <Box className="flex items-center gap-1">
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e293b' }}>{entry.action}</Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', ml: 'auto' }}>{fmtTime(entry.timestamp)}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{entry.detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {auditLog.length === 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1', textAlign: 'center' }}>
                    No session activity yet. Actions will appear here as you interact.
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 1 }} />
              <Box className="flex items-center gap-1">
                <Shield sx={{ fontSize: 12, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8' }}>
                  All email configuration changes are audit-logged.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Section>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailSettings;
